// `isFailedEnvelope` exists because generated 200 types still make `error`
// and `data` optional, and `exactOptionalPropertyTypes` treats a missing key
// as different from `null`. ISSUE-648 made an empty non-2xx a real error
// envelope in `packages/codegen`. This helper still normalizes both keys
// with `?? null` so a confirm with no data cannot look like a success.
// `mapPendingOutcome` returns `null` for any refusal, including
// `FORBIDDEN_ROLE`, per `## 5.6` of ISSUE-649's plan: the gate must never
// disclose a pending outcome to a plain member, so a role refusal renders
// nothing rather than an error.

import type { useContestList } from "@sportsmet/contest-sdk/private";

import { showApiError } from "../../errors/apiErrorBus";
import type {
  typeConfirmGateEntrant,
  typeConfirmGateOutstandingRound,
  typeConfirmGatePendingOutcome,
  typeConfirmGatePickemStanding,
} from "./confirmGateState";

export const CONTEST_CONFIRM_FORBIDDEN_ROLE_CODE = "FORBIDDEN_ROLE";

// ISSUE-864. This row's status used to be typed as bare `string`, so the
// literal below was never checked against the enum and a typo or a rename of
// the member left typecheck green while the confirm gate went dark. Deriving
// the union from the SDK hook makes any drift a compile error at this call
// site. `typeContestListOutput200` is not re-exported from
// `@sportsmet/contest-sdk/private`, so the hook return type is the seam.
type typeContestListItem = NonNullable<
  NonNullable<ReturnType<typeof useContestList>["data"]>["data"]
>["items"][number];

export type typeSdkEnvelope = {
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  } | null;
  data?: unknown;
};

/**
 * `packages/contest-sdk` 200 types still make `error` and `data` optional.
 * ISSUE-648 made an empty non-2xx a real error envelope, but both keys are
 * still normalized with `?? null` here so a confirm with no data cannot look
 * like a success. Every call site in this feature goes through here.
 */
export function isFailedEnvelope(
  result: typeSdkEnvelope | null | undefined,
): boolean {
  if (result === null || result === undefined) return true;
  return (result.error ?? null) !== null || (result.data ?? null) === null;
}

export function raiseApiFailure(result: unknown): void {
  const envelope = result as typeSdkEnvelope | null | undefined;
  const context =
    envelope?.error?.code === "CONFLICT" &&
    envelope.error.details?.reason === "outstandingMakeupDebt"
      ? "contestMakeupDebt"
      : undefined;
  const raised = showApiError(result, context);
  if (!raised) {
    showApiError({ error: { code: "INTERNAL_ERROR" } });
  }
}

export function isForbiddenRoleCode(code: string | null | undefined): boolean {
  return code === CONTEST_CONFIRM_FORBIDDEN_ROLE_CODE;
}

export function selectPendingContestId(
  result:
    | {
        data?: null | {
          items: readonly Pick<typeContestListItem, "contestId" | "status">[];
        };
      }
    | undefined,
): string | null {
  if (result?.data == null) return null;
  const match = result.data.items.find(
    (item) => item.status === "pendingOutcome",
  );
  return match?.contestId ?? null;
}

export type typeConfirmGateFetchedOutcome = {
  outcome: typeConfirmGatePendingOutcome;
  detectedAtMs: number;
  nudgeSentAtMs: number | null;
  commissionerNamedAtMs: number | null;
};

type typePendingOutcomeData = {
  tiebreaker: typeConfirmGatePendingOutcome["tiebreaker"];
  tieExists: boolean;
  medalName: string;
  winners: (typeConfirmGateEntrant & { userId?: string })[];
  tiedCandidates: (typeConfirmGateEntrant & { userId?: string })[];
  commissionerNamedWinnerIds?: string[] | null;
  commissionerNamedAtMs?: number | null;
  isSeasonLastContest: boolean;
  detectedAt: string;
  nudgeSentAt?: string | null;
  outstandingRound?: { roundId: string; eventIds: string[] } | null;
  pickemStandings?:
    (typeConfirmGatePickemStanding & { entryId: string })[] | null;
};

/**
 * ISSUE-868. `outstandingRound` is omitted entirely when there is none, so a
 * missing key and an explicit `null` both mean "nothing outstanding". An
 * empty `eventIds` is also treated as nothing: since ISSUE-819's QA round the
 * service defines outstanding as ungraded AND non-empty, so an empty list
 * contradicts the contract, and a "0 games" line would be worse than silence.
 */
function mapOutstandingRound(
  raw: { roundId: string; eventIds: string[] } | null | undefined,
): typeConfirmGateOutstandingRound | null {
  if (raw == null) return null;
  if (raw.eventIds.length === 0) return null;
  return { roundId: raw.roundId, eventIds: [...raw.eventIds] };
}

export function mapPendingOutcome(
  result: typeSdkEnvelope | null | undefined,
): typeConfirmGateFetchedOutcome | null {
  if (isFailedEnvelope(result)) return null;

  const data = result?.data as typePendingOutcomeData;

  // ISSUE-1309. pickemStandings is null on a survivor contest and an array
  // (possibly empty) on a pick'em one, so its presence is the contest type.
  const isPickem = data.pickemStandings != null;
  const standingByEntryId = new Map(
    (data.pickemStandings ?? []).map((standing) => [
      standing.entryId,
      {
        points: standing.points,
        titles: standing.titles,
        wins: standing.wins,
        finalRoundGuess: standing.finalRoundGuess ?? null,
        finalRoundGuessDiff: standing.finalRoundGuessDiff ?? null,
      },
    ]),
  );
  const toEntrant = (
    entrant: typeConfirmGateEntrant,
  ): typeConfirmGateEntrant => {
    const standing = standingByEntryId.get(entrant.entryId);
    return {
      entryId: entrant.entryId,
      displayName: entrant.displayName,
      ...(standing === undefined ? {} : { pickemStanding: standing }),
    };
  };

  return {
    outcome: {
      tiebreaker: data.tiebreaker,
      tieExists: data.tieExists,
      medalName: data.medalName,
      winners: data.winners.map(toEntrant),
      tiedCandidates: data.tiedCandidates.map(toEntrant),
      ...(isPickem ? { isPickem: true } : {}),
      commissionerNamedWinnerIds: data.commissionerNamedWinnerIds ?? null,
      isSeasonLastContest: data.isSeasonLastContest,
      outstandingRound: mapOutstandingRound(data.outstandingRound),
    },
    detectedAtMs: Date.parse(data.detectedAt),
    nudgeSentAtMs:
      data.nudgeSentAt == null ? null : Date.parse(data.nudgeSentAt),
    commissionerNamedAtMs: data.commissionerNamedAtMs ?? null,
  };
}
